// src/theme/createInternalTheme.ts
//
// The internal console's theme. Deliberately a fraction of the size of the
// product's createFintelaTheme: that file tunes two dozen MUI components for a
// customer-facing product with dialogs, wizards and marketing surfaces. This
// app is tables, charts and tiles.
//
// Five decisions distinguish it, and each is about the app being an INSTRUMENT:
//
//  1. Indigo primary (see ./palette) — it must not be mistaken for the product.
//  2. Radius in TWO STEPS from the surface preset: `sm` (4) for controls, `md`
//     (6) for panels. Denser than the product, and no longer a private const
//     contradicting ./surfaces.
//  3. `size="small"` as the default for controls, for the same reason.
//  4. `font-variant-numeric: tabular-nums` GLOBALLY, not per tile. Every figure
//     here exists to be compared down a column; proportional digits make a
//     column ragged, and remembering to opt in per component is exactly the
//     kind of thing that gets forgotten in the panel that matters.
//  5. A REAL TYPOGRAPHIC RANK. Before this, the wordmark and every panel title
//     were both `variant="h4"` at 0.95rem and no screen had an `<h1>`. The five
//     custom variants below come from ./tokens and carry a `variantMapping`, so
//     `variant="pageTitle"` states intent at the call site AND emits the right
//     element.
//
// ─── AND WHY `surface` IS PASSED IN ─────────────────────────────────────────
// ./surfaces declares `Theme { surface: SurfacePreset }` through MUI's module
// augmentation, and until now nothing ever assigned it — so the type said the
// field existed and `glassSurface(theme)` would have thrown at runtime. Passing
// it here makes the augmentation true. Components still read it through
// `surfaceOf(theme)` in ./tokens, which falls back to the module constant for
// the one place that renders without a ThemeProvider on purpose.

import { createTheme, type Theme } from '@mui/material'
import { MONO, SANS, fontCssVars } from './typography'
import { internalDark, internalLight } from './palette'
import { surface } from './surfaces'
import { motion, typeScale } from './tokens'
import { neutral } from './brand'

export type Mode = 'light' | 'dark'

/** The five ranks this app adds on top of MUI's own. See decision 5. */
declare module '@mui/material/styles' {
  interface TypographyVariants {
    pageTitle: React.CSSProperties
    sectionTitle: React.CSSProperties
    eyebrow: React.CSSProperties
    figure: React.CSSProperties
    figureSm: React.CSSProperties
  }
  interface TypographyVariantsOptions {
    pageTitle?: React.CSSProperties
    sectionTitle?: React.CSSProperties
    eyebrow?: React.CSSProperties
    figure?: React.CSSProperties
    figureSm?: React.CSSProperties
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    pageTitle: true
    sectionTitle: true
    eyebrow: true
    figure: true
    figureSm: true
  }
}

export function createInternalTheme(mode: Mode): Theme {
  const palette = mode === 'dark' ? internalDark : internalLight
  const r = surface.radius

  return createTheme({
    palette: { mode, ...palette },
    shape: { borderRadius: r.sm },
    shadows: surface.shadows,
    // The augmentation in ./surfaces stops being a claim and becomes a fact.
    surface,
    transitions: {
      duration: {
        shortest: motion.duration.instant,
        shorter: motion.duration.fast,
        short: motion.duration.base,
        standard: motion.duration.base,
        complex: motion.duration.slow,
        enteringScreen: motion.duration.base,
        leavingScreen: motion.duration.fast,
      },
      easing: {
        easeInOut: motion.easing.standard,
        easeOut: motion.easing.decelerate,
        easeIn: motion.easing.accelerate,
        sharp: motion.easing.smooth,
      },
    },
    typography: {
      fontFamily: SANS,
      // One step down from the product across the board. More rows on screen is
      // worth more here than presence.
      fontSize: 13,
      // h1..h6 are set EXPLICITLY rather than left to MUI's coefficient. With
      // `fontSize: 13` that coefficient produced h5 at 22.3px and h6 at 18.6px
      // — sizes nobody chose, and h5 was what every figure in the app rendered
      // at.
      h1: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.2 },
      h2: { fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.25 },
      h3: { fontSize: '1.0625rem', fontWeight: 600, letterSpacing: '-0.005em', lineHeight: 1.3 },
      h4: { fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.35 },
      h5: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.4 },
      h6: { fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.4 },
      subtitle2: { fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.01em' },
      body2: { fontSize: '0.8125rem' },
      caption: { fontSize: '0.75rem', lineHeight: 1.4 },
      button: { textTransform: 'none', fontWeight: 600 },
      ...typeScale,
    },
    components: {
      MuiTypography: {
        defaultProps: {
          // So `variant="pageTitle"` emits an <h1> and the document finally has
          // an outline. Without this every custom variant renders as <span>.
          variantMapping: {
            pageTitle: 'h1',
            sectionTitle: 'h2',
            eyebrow: 'p',
            figure: 'p',
            figureSm: 'p',
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            ...fontCssVars,
            '--font-mono': MONO,
          },
          // See decision 4 in the header. Global, on purpose.
          body: {
            fontVariantNumeric: 'tabular-nums',
            fontFeatureSettings: '"tnum" 1',
          },
          // A monitoring wall lives on a screen for hours; a scrollbar that
          // matches the ground is one less thing pulling the eye.
          '*::-webkit-scrollbar': { width: 10, height: 10 },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: mode === 'dark' ? neutral[700] : neutral[300],
            borderRadius: r.lg,
          },
          '*::-webkit-scrollbar-corner': { background: 'transparent' },
        },
      },
      MuiButton: { defaultProps: { size: 'small', disableElevation: true } },
      MuiIconButton: { defaultProps: { size: 'small' } },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiSelect: { defaultProps: { size: 'small' } },
      MuiChip: { defaultProps: { size: 'small' }, styleOverrides: { root: { borderRadius: r.sm } } },
      MuiToggleButtonGroup: { defaultProps: { size: 'small' } },
      MuiTable: { defaultProps: { size: 'small' } },
      MuiPaper: { defaultProps: { elevation: 0 }, styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: ({ theme }) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: r.md,
          }),
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme }) => ({ borderColor: theme.palette.divider }),
          head: ({ theme }) => ({
            fontWeight: 600,
            color: theme.palette.text.secondary,
            whiteSpace: 'nowrap',
          }),
        },
      },
      MuiTooltip: { defaultProps: { arrow: true, enterDelay: 400 } },
      MuiLink: { defaultProps: { underline: 'hover' } },
      MuiAlert: { styleOverrides: { root: { borderRadius: r.sm } } },
      MuiTabs: { defaultProps: { textColor: 'inherit' } },
    },
  })
}
